import React from 'react';
import CustomMedia from 'util/custom-media-query';
import { css } from 'styled-components';
import { applyStyles, isSP } from 'util/helpers';
import FontStyles from 'constants/font-styles';
import Colors from 'constants/colors';
import { Form } from 'react-final-form';
import i18n from 'i18n';
import { isEqual } from 'lodash';
import ConfirmDialog from './ConfirmDialog';
import Button from 'components/common/Button';

class FormDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      visible: false
    };
    this.confirmDialogRef = React.createRef();
  }

  componentWillUnmount() {
    this.clearBackgroundFixed();
  }

  backgroundFixed = () => {
    const beforeClientWidth = document.body.clientWidth;
    document.body.style.overflow = 'hidden';
    const scrollbarWidth = document.body.clientWidth - beforeClientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }
  };

  clearBackgroundFixed = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  open = () => this.setState({ visible: true }, () => this.backgroundFixed());

  close = () => {
    const { onClose } = this.props;
    this.setState({ visible: false }, () => this.clearBackgroundFixed());
    onClose && onClose();
  };

  handleSubmit = values => {
    const { onSubmit } = this.props;
    this.setState({ visible: false }, () => this.clearBackgroundFixed());
    onSubmit && onSubmit(values);
  };

  handleClickCloseButton(showCloseAlert, formRenderProps) {
    if (
      showCloseAlert &&
      !isEqual(formRenderProps.initialValues, formRenderProps.values)
    ) {
      this.confirmDialogRef.current.open();
    } else {
      return this.close();
    }
  }

  render() {
    const {
      className,
      dialogClassName = "dialog",
      title,
      renderBody,
      renderFooter,
      initialValues,
      showCloseAlert,
      hideCloseButton
    } = this.props;

    if (!this.state.visible) {
      return null;
    }

    return (
      <Form
        onSubmit={values => this.handleSubmit(values)}
        initialValues={initialValues}
        close={() => this.close()}
        render={formRenderProps => (
          <div className={className}>
            <div className={dialogClassName}>
              {!hideCloseButton && <Button
                type="button"
                buttonType="Close"
                onClick={() =>
                  this.handleClickCloseButton(showCloseAlert, formRenderProps)
                }
                className="btn-close"
              />}
              {isSP() ? (
                <div className="dialog-body">
                  {title ? <h2 className="dialog-header">{title}</h2> : null}
                  {renderBody && renderBody(formRenderProps)}
                  {renderFooter && (
                    <div className="dialog-footer">
                      {renderFooter(formRenderProps)}
                    </div>
                  )}
                </div>
              ) : (
                <span>
                  {title ? <h2 className="dialog-header">{title}</h2> : null}
                  <div className="dialog-body">
                    {renderBody && renderBody(formRenderProps)}
                  </div>
                  {renderFooter && (
                    <div className="dialog-footer">
                      {renderFooter(formRenderProps)}
                    </div>
                  )}
                </span>
              )}
            </div>
            <ConfirmDialog
              innerRef={this.confirmDialogRef}
              title={i18n.t(
                'orderAggregators:common:order.alertCloseModalTitle'
              )}
              message={i18n.t('orderAggregators:common:order.alertCloseModal')}
              onSubmit={() => this.close()}
            />
          </div>
        )}
      />
    );
  }
}

const commonStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);

  .dialog {
    position: relative;
    background-color: ${Colors.white1};
    border: 1px solid ${Colors.black1};
  }

  .mini-dialog {
    position: relative;
    background-color: ${Colors.white1};
    border: 1px solid ${Colors.black1};
  }

  .dialog-header {
    ${FontStyles.text2};

    margin: 0;
    background-color: ${Colors.gray4};
    color: ${Colors.black1};
  }

  .dialog-body {
    overflow: scroll;
  }

  .dialog-footer {
    background-color: ${Colors.gray4};
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .dialog {
    width: calc(100% - 160px);
    height: calc(100% - 160px);
    max-width: 774px;
    max-height: 762px;
    margin: 80px;
    border-radius: 16px;
  }

  .mini-dialog {
    width: calc(100% - 160px);
    max-width: 774px;
    margin: 80px;
    border-radius: 16px;
  }

  .dialog-header {
    height: 72px;
    padding-left: 32px;
    line-height: 72px;
    border-radius: 16px 16px 0 0;
  }

  .dialog-body {
    border-bottom: 1px solid ${Colors.gray3};
    height: calc(100% - 88px);
  }

  .dialog-header + .dialog-body {
    height: calc(100% - 160px);
  }

  .dialog-footer {
    height: 88px;
    border-radius: 0 0 16px 16px;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  .dialog {
    width: calc(100% - 24px);
    height: calc(100% - 48px);
    max-height: 615px;
    margin: 12px;
    border-radius: 8px;

    .btn-close {
      z-index: 1000;
    }
  }

  .mini-dialog {
    width: calc(100% - 24px);
    margin: 12px;
    border-radius: 8px;

    .btn-close {
      z-index: 1000;
    }
  }

  .dialog-header {
    height: 56px;
    padding-left: 16px;
    line-height: 56px;
    border-radius: 8px 8px 0 0;
  }

  .dialog-body {
    height: 100%;
  }

  .dialog-header + .dialog-body {
    height: calc(100% - 128px);
  }

  .dialog-footer {
    border-top: 1px solid ${Colors.gray3};
    height: 72px;
    border-radius: 0 0 8px 8px;
  }
`;

export default applyStyles(FormDialog)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/FormDialogRenderer.js